<template>
  <div class="acceptance-page">
    <!-- Header -->
    <v-sheet color="red-darken-3" class="px-4 px-md-6 py-3">
      <router-link :to="back.to" class="acceptance-back text-caption">
        <v-icon size="14">mdi-arrow-left</v-icon>
        {{ $t(back.key) }}
      </router-link>
      <div class="d-flex flex-wrap align-center mt-1">
        <span class="text-h6 font-weight-bold mr-3">
          {{ $t("acceptance.page.title")
          }}<template v-if="vehicleName"> · {{ vehicleName }}</template>
        </span>
        <v-chip v-if="dossier" size="small" variant="flat" :color="statusColor">
          {{ $t(`acceptance.status.${dossier.status}`) }}
        </v-chip>
        <span v-if="dirty" class="text-caption ml-3">
          <v-icon size="10" color="amber">mdi-circle</v-icon>
          {{ $t("acceptance.page.unsaved") }}
        </span>
      </div>
      <div v-if="car" class="text-caption font-weight-medium mt-1">
        {{ $t("acceptance.page.from_vehicle") }}:
        {{ $t("acceptance.page.id") }} {{ shortCarId }}
        <template v-if="car.targa">
          · {{ $t("acceptance.page.plate") }} {{ car.targa }}
        </template>
        <template v-if="car.vin">
          · {{ $t("acceptance.page.vin") }} {{ car.vin }}
        </template>
        ·
        {{
          car.status === 1
            ? $t("acceptance.page.inventory")
            : $t("acceptance.page.removed")
        }}
      </div>
    </v-sheet>

    <v-container fluid class="pa-4 pa-md-6">
      <!-- Loading -->
      <v-card v-if="loading" class="pa-4">
        <div class="text-overline text-grey-darken-1">
          {{ $t("acceptance.page.loading") }}
        </div>
        <v-skeleton-loader type="heading, text, text" />
      </v-card>

      <!-- Not found (404, another institution, or load failure) -->
      <v-card v-else-if="notFound" class="pa-8 text-center">
        <v-icon size="48" color="grey-darken-1"
          >mdi-clipboard-remove-outline</v-icon
        >
        <div class="text-h6 mt-3">
          {{ $t("acceptance.page.not_found_title") }}
        </div>
        <div class="text-body-2 text-grey-darken-1 mt-1 mb-4">
          {{ $t("acceptance.page.not_found_body") }}
        </div>
        <v-btn color="primary" variant="flat" to="/vehicle-entry">
          {{ $t("acceptance.page.back_to_list") }}
        </v-btn>
      </v-card>

      <template v-else-if="dossier">
        <!-- Someone else saved first (409): input stays visible until reload. -->
        <v-alert
          v-if="sessionState.conflict"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          <div class="d-flex flex-wrap align-center">
            <div class="mr-auto py-1">
              <strong>{{ $t("acceptance.conflict.title") }}</strong>
              {{ $t("acceptance.conflict.body") }}
            </div>
            <v-btn color="primary" variant="flat" @click="load">
              {{ $t("acceptance.conflict.reload") }}
            </v-btn>
          </div>
        </v-alert>

        <v-alert
          v-if="saveError"
          type="error"
          variant="tonal"
          class="mb-4"
          closable
          @click:close="saveError = ''"
        >
          {{ saveError }}
        </v-alert>

        <!-- PRA on and confirmed (decision Q2): the cards below fold behind this strip. -->
        <acceptance-summary
          v-if="praLayout"
          v-model:expanded="showDetails"
          :class="{ 'acceptance-summary-open': showDetails }"
          :dossier="dossier"
        />

        <!-- In the PRA layout the expanded cards continue the strip's card, as one block. -->
        <div
          v-show="!praLayout || showDetails"
          :class="{ 'acceptance-details elevation-1': praLayout }"
        >
          <deadline-cards :dossier="dossier" />

          <v-row>
            <v-col cols="12" md="8">
              <!-- Workshop on, PRA off: the Bonifica card leads the confirmed page. -->
              <depollution-card
                v-if="showDepollutionCard && !praLayout"
                :state="workshopCardState"
                :view="workshopView"
                :conflict="workshopConflict"
                :deciding="workshopDecision.saving"
                :waste-enabled="wasteEnabled"
                :release="releaseView"
                :release-state="releaseLoad"
                :release-saving="releaseAction.saving"
                @reload="loadWorkshopChecklist"
                @accept="openWorkshopAccept"
                @approve="openWorkshopApprove"
                @correct="openWorkshopCorrection"
                @release="openCarBodyRelease"
                @cancel-release="openCarBodyCancel"
              />
              <arrival-card
                :values="form"
                :readonly="locked"
                :highlight="highlight"
                :frozen="isConfirmed"
                :corrections="corrections"
                :can-correct="canCorrect && !locked"
                :correct-disabled="sessionState.busy"
                @correct="openCorrection"
                @open-log="openLog"
                @change="setField"
              />
              <identity-card
                :values="form"
                :car="car"
                :readonly="locked"
                :highlight="highlight"
                :frozen="isConfirmed"
                :corrections="corrections"
                :can-correct="canCorrect && !locked"
                :correct-disabled="sessionState.busy"
                @correct="openCorrection"
                @open-log="openLog"
                @change="setField"
              />
              <parties-card
                :values="form"
                :readonly="locked"
                :highlight="highlight"
                :frozen="isConfirmed"
                :corrections="corrections"
                :can-correct="canCorrect && !locked"
                :correct-disabled="sessionState.busy"
                @correct="openCorrection"
                @open-log="openLog"
                @change="setField"
              />
              <checklist-card
                :lines="dossier.checklist"
                :values="form"
                :readonly="locked"
                :highlight="highlight"
                :files="files"
                :can-attach="canAddFile"
                :uploading-code="uploadingCode"
                :removable-ids="removableIds"
                :frozen="isConfirmed"
                :corrections="corrections"
                :can-correct="canCorrect && !locked"
                :correct-disabled="sessionState.busy"
                @correct="openCorrection"
                @open-log="openLog"
                @change="setField"
                @attach="attachFile"
                @open="openFile"
                @remove="askRemove"
              />
              <weighings-card
                :weighings="dossier.weighings"
                :current-id="dossier.currentWeighingId"
                :disabled="!canAddWeighing"
                @add="openWeighingDialog"
              />
              <photos-card
                :photos="dossier.photos"
                :files="files"
                :blobs="blobs"
                :disabled="!canAddFile"
                :uploading="uploadingPhoto"
                :removable-ids="removableIds"
                @upload="uploadPhoto"
                @open="openFile"
                @remove="askRemove"
              />
              <hazards-card
                :values="form"
                :readonly="locked"
                :highlight="highlight"
                @change="setField"
              />
              <pra-case v-if="praEnabled && isDraft" state="draft" />
              <history-card
                v-if="!praLayout"
                :dossier-id="dossier.id"
                :refresh-key="historyKey"
                :focus-event-id="focusEventId"
              />
            </v-col>
            <v-col cols="12" md="4">
              <div class="acceptance-sticky">
                <validation-panel
                  :validation="validation"
                  :lines="dossier.checklist"
                  :confirmed-warnings="
                    dossier.status === 'confirmed'
                      ? dossier.warningsAtConfirm || []
                      : null
                  "
                  @select="scrollTo"
                />
              </div>
            </v-col>
          </v-row>
        </div>

        <template v-if="praLayout">
          <owner-compact :dossier="dossier" />
          <depollution-card
            v-if="showDepollutionCard"
            :state="workshopCardState"
            :view="workshopView"
            :conflict="workshopConflict"
            :deciding="workshopDecision.saving"
            :waste-enabled="wasteEnabled"
            :release="releaseView"
            :release-state="releaseLoad"
            :release-saving="releaseAction.saving"
            @reload="loadWorkshopChecklist"
            @accept="openWorkshopAccept"
            @approve="openWorkshopApprove"
            @correct="openWorkshopCorrection"
            @release="openCarBodyRelease"
            @cancel-release="openCarBodyCancel"
          />
          <pra-case
            :state="praCardState"
            :view="praView"
            :conflict="Boolean(praSessionState.conflict)"
            :busy="praSessionState.busy"
            :user="praUser"
            @reload="loadPraCase"
            @record-crd="openCrdDialog"
            @record-request="openRequestDialog"
            @record-receipt="openReceiptDialog"
            @place-hold="openHoldDialog"
            @release-hold="openReleaseDialog"
            @open-file="openInTab"
            @replace-file="openPraFileDialog"
            @remove-file="openPraRemoveDialog"
            @correct="openPraCorrection"
            @open-log="openLog"
          />
          <history-card
            :dossier-id="dossier.id"
            :refresh-key="historyKey"
            :focus-event-id="focusEventId"
          />
        </template>
      </template>
    </v-container>

    <!-- A draft always has its footer; a confirmed dossier shows a save bar only while it has
         unsaved edits (decision D11). -->
    <div
      v-if="dossier && !loading && !notFound && (isDraft || dirty)"
      class="acceptance-footer"
    >
      <span
        v-if="isDraft && dirty"
        class="text-caption text-grey-darken-1 align-self-center mr-auto"
      >
        {{ $t("acceptance.page.save_before_confirm") }}
      </span>
      <span
        v-if="quarantineBlocked"
        class="text-caption text-red-darken-2 align-self-center mr-auto"
      >
        {{ $t("acceptance.page.quarantine_needed") }}
      </span>
      <v-btn
        v-if="isConfirmed"
        color="primary"
        variant="text"
        :disabled="saving"
        @click="cancelEdits"
      >
        {{ $t("acceptance.page.cancel_changes") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="outlined"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        {{
          isConfirmed
            ? $t("acceptance.page.save_changes")
            : $t("acceptance.page.save_draft")
        }}
      </v-btn>
      <v-btn
        v-if="isDraft"
        color="primary"
        variant="flat"
        :disabled="!canConfirm"
        @click="openConfirm"
      >
        {{ $t("acceptance.page.confirm") }}
      </v-btn>
    </div>

    <confirm-dialog
      v-model="confirmDialog"
      :validation="validation"
      :lines="dossier ? dossier.checklist : []"
      :saving="confirming"
      :error="confirmError"
      @confirm="submitConfirm"
    />

    <correction-dialog
      v-model="correctionDialog"
      :group="correctionGroup"
      :values="saved"
      :lines="dossier ? dossier.checklist : []"
      :max-date="maxArrivalDate"
      :saving="correctionSaving"
      :error="correctionError"
      :preview="correctionPreview"
      @submit="submitCorrection"
      @preview="previewCorrection"
      @update:model-value="onCorrectionToggle"
    />

    <!-- A PRA correction (Stage 2): the same dialog with the PRA fields and live date limits;
         every change is previewed with a dry run through the case session. -->
    <correction-dialog
      v-model="praCorrectionDialog"
      :group="praCorrectionGroup"
      :values="praCorrectionValues"
      :fieldset="praFieldset"
      :limits="praCorrectionLimits"
      :saving="praCorrectionSaving"
      :error="praCorrectionError"
      :preview="praCorrectionPreview"
      @submit="submitPraCorrection"
      @preview="previewPraCorrection"
      @update:model-value="onPraCorrectionToggle"
    />

    <pra-crd-dialog
      v-model="crdDialog"
      :saving="crdSaving"
      :error="crdError"
      :vehicle="praVehicle"
      :arrival-date="(dossier && dossier.arrival.date) || ''"
      @submit="submitCrd"
    />

    <pra-request-dialog
      v-model="requestDialog"
      :saving="requestSaving"
      :error="requestError"
      :vehicle="praVehicle"
      :crd-date="praCrdDate"
      :due-date="(praView && praView.dueDate) || ''"
      @submit="submitRequest"
    />

    <pra-receipt-dialog
      v-model="receiptDialog"
      :saving="receiptSaving"
      :error="receiptError"
      :vehicle="praVehicle"
      :request-date="praRequestDate"
      @submit="submitReceipt"
    />

    <pra-hold-dialog
      v-model="holdDialog"
      :saving="holdSaving"
      :error="holdError"
      @submit="submitHold"
    />

    <pra-release-dialog
      v-model="releaseDialog"
      :saving="releaseSaving"
      :error="releaseError"
      :pra-case="praView && praView.praCase"
      @submit="submitRelease"
    />

    <pra-file-dialog
      v-model="praFileDialog.open"
      :saving="praFileSaving"
      :error="praFileError"
      :current="praFileDialog.file"
      :reason-required="praFileDialog.reasonRequired"
      :mode="praFileDialog.mode"
      @submit="submitPraFile"
    />

    <add-weighing-dialog
      v-model="weighingDialog"
      :saving="weighingSaving"
      :error="weighingError"
      :verifier="verifier"
      @submit="submitWeighing"
    />

    <!-- Bonifica card (Stage 3a slice 8): accept an exception or approve, through the checklist
         session. -->
    <workshop-decision-dialog
      v-model="workshopDecision.open"
      :mode="workshopDecision.mode"
      :task-label="
        workshopDecision.taskCode
          ? $t(`workshop.task.${workshopDecision.taskCode}`)
          : ''
      "
      :reason="workshopDecision.reason"
      :timing="workshopTiming"
      :saving="workshopDecision.saving"
      :error="workshopDecision.error"
      @confirm="decideWorkshop"
    />

    <!-- Bonifica card (Stage 3a slice 9): correct a recorded value (an exception's reason) with the
         workshop fieldset and a dry run, through the checklist session. -->
    <correction-dialog
      v-model="workshopCorrection.open"
      :group="workshopCorrection.field"
      :values="workshopCorrectionValues"
      :fieldset="workshopCorrectionFieldset"
      :saving="workshopCorrection.saving"
      :error="workshopCorrection.error"
      :preview="workshopCorrection.preview"
      @submit="submitWorkshopCorrection"
      @preview="previewWorkshopCorrection"
      @update:model-value="onWorkshopCorrectionToggle"
    />

    <!-- Bonifica card (Stage 6 slice 4): release the car body or cancel the release, through the
         release session (its own revision). -->
    <car-body-release-dialog
      v-model="releaseAction.open"
      :mode="releaseAction.mode"
      :view="releaseView"
      :saving="releaseAction.saving"
      :error="releaseAction.error"
      @confirm="submitCarBodyAction"
    />

    <!-- Leaving with unsaved edits -->
    <v-dialog v-model="leaveDialog" max-width="440" persistent>
      <v-card class="pa-2">
        <v-card-title class="text-h6">
          {{ $t("acceptance.page.leave_title") }}
        </v-card-title>
        <v-card-text>{{ $t("acceptance.page.leave_body") }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" color="primary" @click="stay">
            {{ $t("acceptance.page.stay") }}
          </v-btn>
          <v-btn variant="flat" color="red-darken-2" @click="leave">
            {{ $t("acceptance.page.leave_confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <file-preview-dialog
      v-model="preview.open"
      :title="previewTitle"
      :file-ids="previewFileIds"
      :start-id="preview.startId"
      :files="files"
      :blobs="blobs"
      :removable-ids="removableIds"
      :disabled="!canAddFile"
      @remove="askRemove"
    />

    <!-- Removing a file deletes its bytes for good; History keeps "File rimosso". -->
    <v-dialog v-model="removeDialog" max-width="440" persistent>
      <v-card class="pa-2">
        <v-card-title class="text-h6">
          {{ $t("acceptance.files.remove_title") }}
        </v-card-title>
        <v-card-text>
          {{ $t("acceptance.files.remove_body", { name: removeTarget.name }) }}
          <!-- After confirm only a manager removes a file, and says why (logged). -->
          <v-text-field
            v-if="isConfirmed"
            v-model="removeReason"
            class="mt-3"
            variant="outlined"
            density="comfortable"
            maxlength="500"
            :label="`${$t('acceptance.files.remove_reason')} *`"
            :hint="$t('acceptance.files.remove_reason_hint')"
            persistent-hint
            :disabled="removing"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="outlined"
            color="primary"
            :disabled="removing"
            @click="removeDialog = false"
          >
            {{ $t("acceptance.files.remove_cancel") }}
          </v-btn>
          <v-btn
            variant="flat"
            color="red-darken-2"
            :loading="removing"
            :disabled="isConfirmed && !removeReason.trim()"
            @click="confirmRemove"
          >
            {{ $t("acceptance.files.remove_confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  acceptWorkshopException,
  addWeighing,
  approveWorkshopChecklist,
  cancelCarBodyRelease,
  confirmDossier,
  correctDossier,
  correctPraCase,
  getCarBodyRelease,
  getDossier,
  getFileBlob,
  getPraCase,
  correctWorkshopResult,
  getWorkshopChecklistByDossier,
  newCommandId,
  patchDossier,
  placePraHold,
  recordPraCrd,
  recordPraReceipt,
  recordPraRequest,
  releaseCarBody,
  releasePraHold,
  removeDossierFile,
  removePraFile,
  replacePraFile,
  uploadDossierFile,
} from "@/api/compliance";
import {
  createDossierSession,
  isConflictError,
} from "@/components/Acceptance/dossierSession.mjs";
import {
  buildSet,
  dirtyPaths,
  formFromDossier,
  mergeAfterSave,
} from "@/components/Acceptance/draftForm.mjs";
import ArrivalCard from "@/components/Acceptance/arrivalCard.vue";
import IdentityCard from "@/components/Acceptance/identityCard.vue";
import PartiesCard from "@/components/Acceptance/partiesCard.vue";
import ChecklistCard from "@/components/Acceptance/checklistCard.vue";
import HazardsCard from "@/components/Acceptance/hazardsCard.vue";
import WeighingsCard from "@/components/Acceptance/weighingsCard.vue";
import AddWeighingDialog from "@/components/Acceptance/addWeighingDialog.vue";
import { MAX_KG } from "@/components/Acceptance/weighingForm.mjs";
import PhotosCard from "@/components/Acceptance/photosCard.vue";
import FilePreviewDialog from "@/components/Acceptance/filePreviewDialog.vue";
import {
  MAX_FILES_PER_TARGET,
  canRemoveFile,
  checkUpload,
  createBlobCache,
} from "@/components/Acceptance/dossierFiles.mjs";
import HistoryCard from "@/components/Acceptance/historyCard.vue";
import ValidationPanel from "@/components/Acceptance/validationPanel.vue";
import ConfirmDialog from "@/components/Acceptance/confirmDialog.vue";
import CorrectionDialog from "@/components/Acceptance/correctionDialog.vue";
import {
  ARRIVAL_DATE_GROUP,
  confirmDay,
  isFrozenPath,
  quarantineIncomplete,
} from "@/components/Acceptance/corrections.mjs";
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import { validationLabel } from "@/components/Acceptance/validationLabels.mjs";
import DeadlineCards from "@/components/Acceptance/deadlineCards.vue";
import AcceptanceSummary from "@/components/Acceptance/acceptanceSummary.vue";
import OwnerCompact from "@/components/Acceptance/ownerCompact.vue";
import PraCase from "@/components/Acceptance/praCase.vue";
import PraCrdDialog from "@/components/Acceptance/praCrdDialog.vue";
import PraFileDialog from "@/components/Acceptance/praFileDialog.vue";
import PraHoldDialog from "@/components/Acceptance/praHoldDialog.vue";
import PraReceiptDialog from "@/components/Acceptance/praReceiptDialog.vue";
import PraReleaseDialog from "@/components/Acceptance/praReleaseDialog.vue";
import PraRequestDialog from "@/components/Acceptance/praRequestDialog.vue";
import DepollutionCard from "@/components/Acceptance/depollutionCard.vue";
import CarBodyReleaseDialog from "@/components/Acceptance/carBodyReleaseDialog.vue";
import { releaseErrorKey } from "@/components/Waste/carBodyRelease.mjs";
import {
  cardState as checklistCardState,
  usesDepollutionCard,
  workshopLoadState,
} from "@/components/Workshop/workshopView.mjs";
import WorkshopDecisionDialog from "@/components/Workshop/workshopDecisionDialog.vue";
import {
  approvalTiming,
  decisionError,
  decisionKey,
} from "@/components/Workshop/workshopDecisions.mjs";
import {
  correctionMessage as workshopCorrectionMessage,
  correctionSubmitKey as workshopCorrectionSubmitKey,
  correctionValues as workshopValuesOf,
  dateOrderTaskCodes,
  workshopFieldset,
} from "@/components/Workshop/workshopCorrections.mjs";
import {
  praFileErrorKey,
  replaceSubmitKey,
} from "@/components/Acceptance/praFiles.mjs";
import {
  crdErrorKey,
  crdSubmitKey,
} from "@/components/Acceptance/praCrdForm.mjs";
import {
  requestErrorKey,
  requestSubmitKey,
} from "@/components/Acceptance/praRequestForm.mjs";
import {
  receiptErrorKey,
  receiptSubmitKey,
} from "@/components/Acceptance/praReceiptForm.mjs";
import {
  holdErrorKey,
  holdSubmitKey,
  releaseSubmitKey,
} from "@/components/Acceptance/praHoldForm.mjs";
import {
  backLink,
  cardState,
  praLoadState,
  usesPraLayout,
} from "@/components/Acceptance/praView.mjs";
import {
  PRA_FIELDSET,
  praCorrectionErrorKey,
  praCorrectionSubmitKey,
  praDateLimits,
  praGroupFields,
  praValues,
} from "@/components/Acceptance/praCorrections.mjs";

// 422 reasons with their own message; anything else shows the API's errorMsg.
const ERROR_REASONS = [
  "future_date",
  "reason_required",
  "not_applicable_not_allowed",
  "quarantine_required",
];

// Weighing 422 reasons with their own message (acceptance.weighings.errors.*).
const WEIGHING_REASONS = [
  "required",
  "invalid_type",
  "too_precise",
  "not_positive",
  "negative", // the API's not_positive on tare (tare may be 0)
  "too_large",
  "net_not_positive",
  "too_long",
  "invalid_date",
  "future_time",
];

// File 422 reasons with their own message (acceptance.files.errors.*).
const FILE_REASONS = [
  "photo_must_be_image",
  "unknown_code",
  "file_limit_reached",
];

// Validation code -> card to scroll to.
const CODE_SECTIONS = {
  vin_missing: "identity",
  class_missing: "identity",
  plate_missing: "identity",
  plate_country_missing: "identity",
  vin_differs_from_car: "identity",
  plate_differs_from_car: "identity",
  vin_normalised_invalid: "identity",
  deliverer_missing: "parties",
  authority_missing: "parties",
  owner_missing: "parties",
  arrival_missing: "arrival",
  arrival_after_confirm_day: "arrival",
  arrival_mode_missing: "arrival",
  transporter_missing: "arrival",
  incoming_fir_missing: "arrival",
  checklist_incomplete: "checklist",
  weighing_missing: "weighings",
  photo_vin_missing: "photos",
  photo_plate_missing: "photos",
  photo_condition_missing: "photos",
  quarantine_missing: "hazards",
};

export default {
  name: "VehicleAcceptance",
  components: {
    "arrival-card": ArrivalCard,
    "identity-card": IdentityCard,
    "parties-card": PartiesCard,
    "checklist-card": ChecklistCard,
    "hazards-card": HazardsCard,
    "weighings-card": WeighingsCard,
    "add-weighing-dialog": AddWeighingDialog,
    "photos-card": PhotosCard,
    "file-preview-dialog": FilePreviewDialog,
    "history-card": HistoryCard,
    "validation-panel": ValidationPanel,
    "confirm-dialog": ConfirmDialog,
    "correction-dialog": CorrectionDialog,
    "deadline-cards": DeadlineCards,
    "acceptance-summary": AcceptanceSummary,
    "owner-compact": OwnerCompact,
    "pra-case": PraCase,
    "depollution-card": DepollutionCard,
    "car-body-release-dialog": CarBodyReleaseDialog,
    "workshop-decision-dialog": WorkshopDecisionDialog,
    "pra-crd-dialog": PraCrdDialog,
    "pra-file-dialog": PraFileDialog,
    "pra-hold-dialog": PraHoldDialog,
    "pra-receipt-dialog": PraReceiptDialog,
    "pra-release-dialog": PraReleaseDialog,
    "pra-request-dialog": PraRequestDialog,
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty || this.leaveConfirmed) {
      next();
      return;
    }
    this.pendingLeave = next;
    this.leaveDialog = true;
  },
  data: () => ({
    loading: true,
    notFound: false,
    dossier: null,
    car: null,
    mismatch: { vin: false, plate: false },
    files: [],
    validation: null,
    // Latest correction per field path, and whether this user may correct (Manager / Admin)
    corrections: {},
    canCorrect: false,
    // Flat draft form keyed by API path, and the values last saved.
    form: {},
    saved: {},
    sessionState: { revision: 0, busy: false, conflict: null },
    saving: false,
    saveError: "",
    historyKey: 0,
    weighingDialog: false,
    weighingSaving: false,
    weighingError: "",
    // "photo:<kind>" or "checklist:<code>" while that upload runs
    uploading: null,
    removeDialog: false,
    removeTarget: { fileId: null, name: "" },
    removeReason: "",
    removing: false,
    correctionDialog: false,
    correctionGroup: null,
    correctionSaving: false,
    correctionError: "",
    // Dry run of the arrival date: { loading, error, data } or null
    correctionPreview: null,
    // History row to highlight after "Valore precedente" is followed
    focusEventId: null,
    confirmDialog: false,
    confirming: false,
    confirmError: "",
    // Image preview of one photo slot ("photo", kind) or checklist line ("checklist", code).
    preview: { open: false, purpose: null, target: null, startId: null },
    leaveDialog: false,
    leaveConfirmed: false,
    // PRA layout (Stage 2, PRA on and confirmed): the Stage 1 cards are folded until expanded.
    showDetails: false,
    // GET /pra-case result and its load state ("loading" | "case" | "no_case" | "error")
    praView: null,
    praLoad: null,
    // The case has its own revision and session, so PRA work never conflicts with yard work.
    praSessionState: { revision: 0, busy: false, conflict: null },
    // Workshop on and confirmed (Stage 3a): GET /workshop-checklist result and its load state
    // ("loading" | "checklist" | "no_checklist" | "error"). The checklist has its own revision and
    // session, so workshop work never conflicts with dossier or PRA work (L20).
    workshopView: null,
    workshopLoad: null,
    workshopSessionState: { revision: 0, busy: false, conflict: null },
    // A checklist 409 that is not a revision conflict (already_approved, not_current, …).
    workshopWriteConflict: false,
    // The Bonifica card's accept / approve dialog (mode "accept" | "approve"; error an i18n key).
    workshopDecision: {
      open: false,
      mode: "accept",
      resultId: null,
      taskCode: null,
      reason: "",
      saving: false,
      error: "",
    },
    // Waste on and the Bonifica card shown (Stage 6 slice 4): GET /car-body-release result and its
    // load state ("loading" | "ok" | "error"). The release has its own revision and session.
    releaseView: null,
    releaseLoad: null,
    // "Rilascia carcassa" / "Annulla rilascio" (mode "release" | "cancel"; error a translated text).
    releaseAction: { open: false, mode: "release", saving: false, error: "" },
    // The Bonifica card's correction of one recorded value (slice 9; preview is the dry run).
    workshopCorrection: {
      open: false,
      field: null,
      taskCode: null,
      resultId: null,
      saving: false,
      error: "",
      preview: null,
    },
    crdDialog: false,
    crdSaving: false,
    crdError: "",
    requestDialog: false,
    requestSaving: false,
    requestError: "",
    receiptDialog: false,
    receiptSaving: false,
    receiptError: "",
    holdDialog: false,
    holdSaving: false,
    holdError: "",
    releaseDialog: false,
    releaseSaving: false,
    releaseError: "",
    // "Sostituisci" / "Rimuovi" on a PRA document: { open, file, reasonRequired, mode }
    praFileDialog: {
      open: false,
      file: null,
      reasonRequired: false,
      mode: "replace",
    },
    praFileSaving: false,
    praFileError: "",
    // A PRA correction (Manager / Admin): the group, and the dry run { loading, error, data }
    praCorrectionDialog: false,
    praCorrectionGroup: null,
    praCorrectionSaving: false,
    praCorrectionError: "",
    praCorrectionPreview: null,
  }),
  computed: {
    dirtyList() {
      return dirtyPaths(this.form, this.saved);
    },
    dirty() {
      return this.dirtyList.length > 0;
    },
    // Unsaved fields are highlighted while a conflict is shown.
    highlight() {
      return this.sessionState.conflict ? this.dirtyList : [];
    },
    // Nothing can be edited without a dossier or while a conflict waits for a reload. A confirmed
    // dossier is not locked as a whole: its frozen fields are (isConfirmed), the rest saves with
    // the save bar.
    locked() {
      return Boolean(!this.dossier || this.sessionState.conflict);
    },
    // The API refuses a hazard without a quarantine decision after confirm.
    quarantineBlocked() {
      return this.isConfirmed && quarantineIncomplete(this.form);
    },
    canSave() {
      return (
        this.dirty &&
        !this.locked &&
        !this.sessionState.busy &&
        !this.quarantineBlocked
      );
    },
    isDraft() {
      return Boolean(this.dossier && this.dossier.status === "draft");
    },
    isConfirmed() {
      return Boolean(this.dossier && this.dossier.status === "confirmed");
    },
    // Latest arrival date a correction may choose: the day the acceptance was confirmed.
    maxArrivalDate() {
      return this.dossier && this.dossier.confirmedAt
        ? confirmDay(this.dossier.confirmedAt)
        : null;
    },
    // Only a saved draft can be confirmed: the API checks the stored dossier, never the form.
    canConfirm() {
      return (
        this.isDraft &&
        !this.dirty &&
        !this.locked &&
        !this.sessionState.busy &&
        !this.confirming
      );
    },
    // Weighings are allowed while draft and after confirm (S1-08).
    canAddWeighing() {
      return Boolean(
        this.dossier && !this.sessionState.conflict && !this.sessionState.busy,
      );
    },
    // Files can be added and removed while draft and after confirm, one action at a time.
    canAddFile() {
      return this.canAddWeighing && !this.uploading && !this.removing;
    },
    // Files the current user may remove: any for a manager, else their own (the API decides).
    removableIds() {
      const user = {
        role: this.$store.getters.role,
        userId: this.$store.getters.userId,
        status: this.dossier && this.dossier.status,
      };
      return this.files
        .filter((file) => canRemoveFile(file, user))
        .map((file) => file.id);
    },
    // The previewed slot's or line's images, newest first; follows the dossier after a removal.
    previewFileIds() {
      const { purpose, target } = this.preview;
      if (!this.dossier || !purpose) return [];
      let ids;
      if (purpose === "photo") {
        ids = this.dossier.photos
          .filter((photo) => photo.kind === target)
          .map((photo) => photo.fileId);
      } else {
        const line = this.dossier.checklist.find(
          (item) => item.code === target,
        );
        ids = (line && line.fileIds) || [];
      }
      return ids
        .filter((id) => {
          const file = this.files.find((item) => item.id === id);
          return Boolean(
            file && String(file.mimetype || "").startsWith("image/"),
          );
        })
        .reverse();
    },
    previewTitle() {
      const { purpose, target } = this.preview;
      if (!this.dossier || !purpose) return "";
      if (purpose === "photo")
        return this.$t(`acceptance.photos.kinds.${target}`);
      const line =
        this.dossier.checklist.find((item) => item.code === target) || {};
      const key = `acceptance.checklist.lines.${target}`;
      return this.$t(key) === key ? line.label || "" : this.$t(key);
    },
    uploadingPhoto() {
      const [purpose, target] = (this.uploading || "").split(":");
      return purpose === "photo" ? target : null;
    },
    uploadingCode() {
      const [purpose, target] = (this.uploading || "").split(":");
      return purpose === "checklist" ? target : null;
    },
    verifier() {
      const name = this.$store.getters.userName || "—";
      const role = this.$store.getters.role;
      return role ? `${name} (${role})` : name;
    },
    vehicleName() {
      if (!this.car) return "";
      return [this.car.brand, this.car.model].filter(Boolean).join(" ");
    },
    shortCarId() {
      return this.car && this.car.id ? this.car.id.slice(-6) : "";
    },
    praEnabled() {
      return this.$store.getters.praEnabled === true;
    },
    praLayout() {
      return usesPraLayout({
        praEnabled: this.praEnabled,
        dossierStatus: this.dossier && this.dossier.status,
      });
    },
    workshopEnabled() {
      return this.$store.getters.workshopEnabled === true;
    },
    showDepollutionCard() {
      return usesDepollutionCard({
        workshopEnabled: this.workshopEnabled,
        dossierStatus: this.dossier && this.dossier.status,
      });
    },
    workshopCardState() {
      return checklistCardState(this.workshopLoad);
    },
    wasteEnabled() {
      return this.$store.getters.wasteEnabled === true;
    },
    // Any checklist 409: the card shows "Ricarica" (only the checklist reloads).
    workshopConflict() {
      return Boolean(
        this.workshopSessionState.conflict || this.workshopWriteConflict,
      );
    },
    workshopTiming() {
      return approvalTiming(this.workshopView);
    },
    // The result the Bonifica card corrects, while it is still the task's current result.
    workshopCorrectionResult() {
      const { taskCode, resultId } = this.workshopCorrection;
      const tasks = (this.workshopView && this.workshopView.tasks) || [];
      const task = tasks.find((item) => item.code === taskCode);
      return task && task.current && task.current.id === resultId
        ? { task, result: task.current }
        : null;
    },
    workshopCorrectionValues() {
      const found = this.workshopCorrectionResult;
      return found ? workshopValuesOf(found.task, found.result) : {};
    },
    workshopCorrectionFieldset() {
      const found = this.workshopCorrectionResult;
      if (!found) return null;
      return workshopFieldset(found.task, found.result, {
        arrivalDate: this.workshopView.arrivalDate,
        locale: this.$i18n.locale,
      });
    },
    praUser() {
      return {
        role: this.$store.getters.role,
        userId: this.$store.getters.userId,
      };
    },
    // "{vehicle} · {plate}" in the PRA step dialogs
    praVehicle() {
      const plate = this.car && this.car.targa;
      return [this.vehicleName, plate].filter(Boolean).join(" · ");
    },
    // The CRD date issued: the earliest request date.
    praCrdDate() {
      const praCase = this.praView && this.praView.praCase;
      return (praCase && praCase.crd && praCase.crd.issuedOn) || "";
    },
    // The request date: the earliest receipt date.
    praRequestDate() {
      const praCase = this.praView && this.praView.praCase;
      return (praCase && praCase.request && praCase.request.date) || "";
    },
    praFieldset() {
      return PRA_FIELDSET;
    },
    // The case's recorded values, flat by path, for the PRA correction dialog.
    praCorrectionValues() {
      return praValues(this.praView && this.praView.praCase);
    },
    // { path: { min, max } } for the date of the group being corrected: the neighbouring recorded
    // dates (arrival <= CRD <= request <= receipt) and today in Europe/Rome.
    praCorrectionLimits() {
      const praCase = this.praView && this.praView.praCase;
      const limits = {};
      praGroupFields(this.praCorrectionGroup).forEach((path) => {
        const range = praDateLimits(path, praCase, {
          arrivalDate: this.dossier && this.dossier.arrival.date,
        });
        if (range) limits[path] = range;
      });
      return limits;
    },
    praCardState() {
      return cardState({
        dossierStatus: this.dossier && this.dossier.status,
        loadState: this.praLoad,
      });
    },
    back() {
      return backLink({
        praEnabled: this.praEnabled,
        carStatus: this.car && this.car.status,
      });
    },
    statusColor() {
      if (!this.dossier) return "grey";
      if (this.dossier.status === "confirmed") return "green";
      if (this.dossier.status === "draft") return "amber";
      return "grey";
    },
  },
  watch: {
    "$route.params.dossierId"(value, oldValue) {
      if (value && value !== oldValue) {
        this.blobs.revokeAll();
        this.showDetails = false;
        this.load();
      }
    },
  },
  created() {
    // Not reactive on purpose: the page mirrors its state in sessionState via onChange.
    this.session = null;
    this.praSession = null;
    // Only the latest PRA case load may update the card.
    this.praLoadSeq = 0;
    this.workshopSession = null;
    // Only the latest checklist load may update the Bonifica card.
    this.workshopLoadSeq = 0;
    // The car-body release session (Stage 6 slice 4), its latest load, and the { key, commandId }
    // of the release or cancel being saved: a retry reuses it.
    this.releaseSession = null;
    this.releaseLoadSeq = 0;
    this.pendingCarBodyAction = null;
    // { key, commandId } of the Bonifica decision being saved: a retry reuses it.
    this.pendingWorkshopDecision = null;
    // The same for a Bonifica correction, and the latest of its dry runs.
    this.pendingWorkshopCorrection = null;
    this.workshopPreviewSeq = 0;
    this.pendingSave = null;
    this.pendingWeighing = null;
    this.pendingUpload = null;
    this.pendingRemove = null;
    this.pendingConfirm = null;
    this.pendingCorrection = null;
    this.pendingCrd = null;
    this.pendingRequest = null;
    this.pendingReceipt = null;
    this.pendingHold = null;
    this.pendingRelease = null;
    this.pendingPraFile = null;
    this.pendingPraCorrection = null;
    this.previewSeq = 0;
    this.praPreviewSeq = 0;
    this.pendingLeave = null;
    // Object URLs of private files, revoked on unmount. Files never change, so a reload of the
    // same dossier keeps them.
    this.blobs = createBlobCache({
      load: getFileBlob,
      revoke: (url) => URL.revokeObjectURL(url),
    });
    this.onBeforeUnload = (event) => {
      if (!this.dirty) return;
      event.preventDefault();
      event.returnValue = ""; // eslint-disable-line no-param-reassign
    };
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.load();
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    this.blobs.revokeAll();
  },
  methods: {
    async load() {
      this.loading = true;
      this.notFound = false;
      this.saveError = "";
      try {
        const result = await getDossier(this.$route.params.dossierId);
        this.applyView(result, null);
        this.pendingSave = null;
        this.pendingWeighing = null;
        this.pendingUpload = null;
        this.pendingRemove = null;
        this.pendingConfirm = null;
        this.pendingCorrection = null;
        this.session = createDossierSession({
          revision: result.revision,
          onChange: (state) => {
            this.sessionState = state;
          },
        });
        this.sessionState = {
          revision: result.revision,
          busy: false,
          conflict: null,
        };
        this.loadPraCase();
        this.loadWorkshopChecklist();
        this.loadCarBodyRelease();
      } catch (err) {
        this.dossier = null;
        this.car = null;
        this.notFound = true;
      } finally {
        this.loading = false;
      }
    },
    // The PRA case, only with PRA on and a confirmed dossier (no request otherwise). Not awaited:
    // a failure stays inside the PRA card and the rest of the page works.
    async loadPraCase() {
      const seq = (this.praLoadSeq += 1);
      if (!this.praLayout) {
        this.praView = null;
        this.praLoad = null;
        return;
      }
      const dossierId = this.dossier.id;
      this.praLoad = "loading";
      try {
        const view = await getPraCase(dossierId);
        if (seq !== this.praLoadSeq) return;
        this.praView = view;
        this.praLoad = "case";
        this.praSession = createDossierSession({
          revision: view.revision,
          onChange: (state) => {
            this.praSessionState = state;
          },
        });
        this.praSessionState = {
          revision: view.revision,
          busy: false,
          conflict: null,
        };
      } catch (err) {
        if (seq !== this.praLoadSeq) return;
        this.praView = null;
        this.praLoad = praLoadState(err);
      }
    },
    // The depollution checklist, only with the workshop on and a confirmed dossier (no request
    // otherwise). Not awaited: a failure stays inside the Bonifica card and the page keeps working.
    async loadWorkshopChecklist() {
      const seq = (this.workshopLoadSeq += 1);
      if (!this.showDepollutionCard) {
        this.workshopView = null;
        this.workshopLoad = null;
        return;
      }
      const dossierId = this.dossier.id;
      this.workshopLoad = "loading";
      try {
        const view = await getWorkshopChecklistByDossier(dossierId);
        if (seq !== this.workshopLoadSeq) return;
        const revision = view.checklist.revision;
        this.workshopView = view;
        this.workshopLoad = "checklist";
        this.workshopSession = createDossierSession({
          revision,
          onChange: (state) => {
            this.workshopSessionState = state;
          },
        });
        this.workshopSessionState = { revision, busy: false, conflict: null };
        this.workshopWriteConflict = false;
        this.pendingWorkshopDecision = null;
        this.pendingWorkshopCorrection = null;
      } catch (err) {
        if (seq !== this.workshopLoadSeq) return;
        this.workshopView = null;
        this.workshopLoad = workshopLoadState(err);
      }
    },
    // The car-body release, only with waste on and the Bonifica card shown (no request otherwise).
    // Not awaited: a failure only hides the release part of the card.
    async loadCarBodyRelease() {
      const seq = (this.releaseLoadSeq += 1);
      if (!this.showDepollutionCard || !this.wasteEnabled) {
        this.releaseView = null;
        this.releaseLoad = null;
        return;
      }
      const dossierId = this.dossier.id;
      this.releaseLoad = "loading";
      try {
        const view = await getCarBodyRelease(dossierId);
        if (seq !== this.releaseLoadSeq) return;
        this.releaseView = view;
        this.releaseLoad = "ok";
        this.releaseSession = createDossierSession({ revision: view.revision });
        this.pendingCarBodyAction = null;
      } catch (err) {
        if (seq !== this.releaseLoadSeq) return;
        this.releaseView = null;
        this.releaseLoad = "error";
      }
    },
    openCarBodyRelease() {
      if (!this.releaseSession || !this.releaseView) return;
      this.releaseAction = {
        open: true,
        mode: "release",
        saving: false,
        error: "",
      };
    },
    openCarBodyCancel() {
      if (!this.releaseSession || !this.releaseView) return;
      this.releaseAction = {
        open: true,
        mode: "cancel",
        saving: false,
        error: "",
      };
    },
    // Runs the release or the cancel through the release session. A 409 reloads the release.
    async submitCarBodyAction(body) {
      const action = this.releaseAction;
      if (!this.releaseSession || action.saving) return;
      const dossierId = this.dossier.id;
      const { mode } = action;
      const key = JSON.stringify([mode, dossierId, body]);
      // A retry of the same action reuses its commandId, so a double click writes it once.
      if (!this.pendingCarBodyAction || this.pendingCarBodyAction.key !== key) {
        this.pendingCarBodyAction = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingCarBodyAction;
      action.saving = true;
      action.error = "";
      try {
        const view = await this.releaseSession.run((revision) =>
          mode === "cancel"
            ? cancelCarBodyRelease(dossierId, revision, commandId, body.reason)
            : releaseCarBody(dossierId, revision, commandId, body),
        );
        this.pendingCarBodyAction = null;
        this.releaseView = view;
        action.open = false;
        // The History card shows the new entry.
        this.historyKey += 1;
      } catch (err) {
        const res = err && err.response;
        const known = releaseErrorKey(err);
        if (res && res.status === 409 && !known) {
          // Someone else changed the release: show the latest state.
          action.open = false;
          this.loadCarBodyRelease();
        } else if (known) {
          action.error = this.$t(known);
          if (res && res.status === 409) this.loadCarBodyRelease();
        } else {
          action.error =
            (res && res.data && res.data.errorMsg) ||
            this.$t("acceptance.car_body.errors.failed");
        }
      } finally {
        action.saving = false;
      }
    },
    // "Accetta" on a pending exception of the Bonifica card: confirm with an optional note.
    openWorkshopAccept(row) {
      if (!this.workshopSession || !row) return;
      this.workshopDecision = {
        ...this.workshopDecision,
        open: true,
        mode: "accept",
        resultId: row.resultId,
        taskCode: row.taskCode,
        reason: row.reason || "",
        error: "",
      };
    },
    // "Approva bonifica" on the Bonifica card: the dialog says "nei tempi" or "in ritardo".
    openWorkshopApprove() {
      if (!this.workshopSession) return;
      this.workshopDecision = {
        ...this.workshopDecision,
        open: true,
        mode: "approve",
        resultId: null,
        taskCode: null,
        reason: "",
        error: "",
      };
    },
    // Runs the confirmed decision through the checklist session (its own revision, L20), never
    // the dossier's. A 409 closes the dialog and the card shows "Ricarica".
    async decideWorkshop(note) {
      const decision = this.workshopDecision;
      if (!this.workshopSession || !this.workshopView || decision.saving) {
        return;
      }
      const checklistId = this.workshopView.checklist.id;
      const { mode, resultId } = decision;
      const key = decisionKey(mode, { checklistId, resultId, note });
      // A retry of the same decision reuses its commandId, so a double click writes it once.
      if (
        !this.pendingWorkshopDecision ||
        this.pendingWorkshopDecision.key !== key
      ) {
        this.pendingWorkshopDecision = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingWorkshopDecision;
      decision.saving = true;
      decision.error = "";
      try {
        const view = await this.workshopSession.run((revision) =>
          mode === "accept"
            ? acceptWorkshopException(
                checklistId,
                resultId,
                revision,
                commandId,
                note,
              )
            : approveWorkshopChecklist(checklistId, revision, commandId),
        );
        this.pendingWorkshopDecision = null;
        this.workshopView = view;
        decision.open = false;
        if (mode === "approve") this.loadCarBodyRelease();
        // The History card shows the new entry.
        this.historyKey += 1;
      } catch (err) {
        const res = err && err.response;
        if (res && res.status === 409) {
          // revision_conflict sets the session's conflict; the other 409s do not.
          if (!isConflictError(err)) this.workshopWriteConflict = true;
          decision.open = false;
        } else if (err && err.code === "conflict_pending") {
          decision.open = false;
        } else {
          decision.error = decisionError(err);
        }
      } finally {
        decision.saving = false;
      }
    },
    // "Correggi" on the Bonifica card (slice 9): { task, field } of a current result.
    openWorkshopCorrection({ task, field }) {
      const current = task && task.current;
      if (!this.workshopSession || !current) return;
      this.workshopPreviewSeq += 1;
      this.pendingWorkshopCorrection = null;
      this.workshopCorrection = {
        open: true,
        field,
        taskCode: task.code,
        resultId: current.id,
        saving: false,
        error: "",
        preview: null,
      };
    },
    onWorkshopCorrectionToggle(open) {
      this.workshopCorrection.open = open;
      if (!open) {
        this.workshopPreviewSeq += 1;
        this.workshopCorrection.preview = null;
      }
    },
    // A 409 closes the correction dialog and the card shows "Ricarica".
    closeWorkshopCorrectionOnConflict(err) {
      const res = err && err.response;
      if (res && res.status === 409) {
        if (!isConflictError(err)) this.workshopWriteConflict = true;
      } else if (!(err && err.code === "conflict_pending")) {
        return false;
      }
      this.workshopCorrection.open = false;
      this.workshopCorrection.preview = null;
      return true;
    },
    // The dry run of a Bonifica correction; request is { group, value } or null.
    async previewWorkshopCorrection(request) {
      this.workshopPreviewSeq += 1;
      const seq = this.workshopPreviewSeq;
      const correction = this.workshopCorrection;
      if (!request || !this.workshopSession || !this.workshopView) {
        correction.preview = null;
        return;
      }
      correction.preview = { loading: true, error: "", data: null };
      const checklistId = this.workshopView.checklist.id;
      const { resultId, field } = correction;
      try {
        const data = await this.workshopSession.run((revision) =>
          correctWorkshopResult(checklistId, revision, newCommandId(), {
            resultId,
            field,
            value: request.value,
            dryRun: true,
          }),
        );
        if (seq === this.workshopPreviewSeq) {
          correction.preview = { loading: false, error: "", data };
        }
      } catch (err) {
        if (
          seq !== this.workshopPreviewSeq ||
          this.closeWorkshopCorrectionOnConflict(err)
        ) {
          return;
        }
        correction.preview = {
          loading: false,
          error: workshopCorrectionMessage(err, (key) => this.$t(key)),
          data: null,
        };
      }
    },
    // Saves a Bonifica correction through the checklist session (its own revision, L20).
    async submitWorkshopCorrection({ group, value, reason }) {
      const correction = this.workshopCorrection;
      if (!this.workshopSession || !this.workshopView || correction.saving) {
        return;
      }
      const checklistId = this.workshopView.checklist.id;
      const { resultId } = correction;
      const key = workshopCorrectionSubmitKey({
        checklistId,
        resultId,
        field: group,
        value,
        reason,
      });
      if (
        !this.pendingWorkshopCorrection ||
        this.pendingWorkshopCorrection.key !== key
      ) {
        this.pendingWorkshopCorrection = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingWorkshopCorrection;
      correction.saving = true;
      correction.error = "";
      try {
        const view = await this.workshopSession.run((revision) =>
          correctWorkshopResult(checklistId, revision, commandId, {
            resultId,
            field: group,
            value,
            reason,
          }),
        );
        this.pendingWorkshopCorrection = null;
        this.workshopView = view;
        correction.open = false;
        // The History card shows the new entry.
        this.historyKey += 1;
      } catch (err) {
        if (!this.closeWorkshopCorrectionOnConflict(err)) {
          correction.error = this.$t("acceptance.corrections.failed", {
            message: workshopCorrectionMessage(err, (k) => this.$t(k)),
          });
        }
      } finally {
        correction.saving = false;
      }
    },
    openCrdDialog() {
      if (!this.praSession || !this.praView) return;
      this.crdError = "";
      this.crdDialog = true;
    },
    // The CRD goes through the case session (the case revision), so it never conflicts with
    // edits of the acceptance. A 409 shows the conflict banner inside the PRA card.
    async submitCrd(form) {
      if (!this.praSession || !this.praView || this.crdSaving) return;
      const key = crdSubmitKey(form);
      // A retry of the same CRD reuses its commandId, so a double submit records it once.
      if (!this.pendingCrd || this.pendingCrd.key !== key) {
        this.pendingCrd = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingCrd;
      const caseId = this.praView.praCase.id;
      this.crdSaving = true;
      this.crdError = "";
      try {
        const result = await this.praSession.run((revision) =>
          recordPraCrd(caseId, revision, commandId, form),
        );
        this.pendingCrd = null;
        this.praView = result;
        this.crdDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.crdDialog = false;
        } else {
          this.crdError = this.crdErrorText(err);
        }
      } finally {
        this.crdSaving = false;
      }
    },
    openRequestDialog() {
      if (!this.praSession || !this.praView) return;
      this.requestError = "";
      this.requestDialog = true;
    },
    // The request goes through the case session, like the CRD. A late request is saved: its
    // section shows the amber chip from the stored lateAtRecording.
    async submitRequest(request) {
      if (!this.praSession || !this.praView || this.requestSaving) return;
      const key = requestSubmitKey(request);
      // A retry of the same request reuses its commandId, so a double submit records it once.
      if (!this.pendingRequest || this.pendingRequest.key !== key) {
        this.pendingRequest = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingRequest;
      const caseId = this.praView.praCase.id;
      this.requestSaving = true;
      this.requestError = "";
      try {
        const result = await this.praSession.run((revision) =>
          recordPraRequest(caseId, revision, commandId, request),
        );
        this.pendingRequest = null;
        this.praView = result;
        this.requestDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.requestDialog = false;
        } else {
          this.requestError = this.requestErrorText(err);
        }
      } finally {
        this.requestSaving = false;
      }
    },
    openReceiptDialog() {
      if (!this.praSession || !this.praView) return;
      this.receiptError = "";
      this.receiptDialog = true;
    },
    // The receipt goes through the case session, like the other steps, and closes the case
    // (Radiata): the card then shows the green banner and no step button.
    async submitReceipt(receipt) {
      if (!this.praSession || !this.praView || this.receiptSaving) return;
      const key = receiptSubmitKey(receipt);
      // A retry of the same receipt reuses its commandId, so a double submit records it once.
      if (!this.pendingReceipt || this.pendingReceipt.key !== key) {
        this.pendingReceipt = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingReceipt;
      const caseId = this.praView.praCase.id;
      this.receiptSaving = true;
      this.receiptError = "";
      try {
        const result = await this.praSession.run((revision) =>
          recordPraReceipt(caseId, revision, commandId, receipt),
        );
        this.pendingReceipt = null;
        this.praView = result;
        this.receiptDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.receiptDialog = false;
        } else {
          this.receiptError = this.receiptErrorText(err);
        }
      } finally {
        this.receiptSaving = false;
      }
    },
    openHoldDialog() {
      if (!this.praSession || !this.praView) return;
      this.holdError = "";
      this.holdDialog = true;
    },
    // The hold goes through the case session, like the steps. The state stays; the card shows the
    // purple banner and the step button disabled until the release.
    async submitHold(hold) {
      if (!this.praSession || !this.praView || this.holdSaving) return;
      const key = holdSubmitKey(hold);
      // A retry of the same hold reuses its commandId, so a double submit places it once.
      if (!this.pendingHold || this.pendingHold.key !== key) {
        this.pendingHold = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingHold;
      const caseId = this.praView.praCase.id;
      this.holdSaving = true;
      this.holdError = "";
      try {
        const result = await this.praSession.run((revision) =>
          placePraHold(caseId, revision, commandId, hold),
        );
        this.pendingHold = null;
        this.praView = result;
        this.holdDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.holdDialog = false;
        } else {
          this.holdError = this.holdErrorText(err, "save_failed");
        }
      } finally {
        this.holdSaving = false;
      }
    },
    openReleaseDialog() {
      if (!this.praSession || !this.praView) return;
      this.releaseError = "";
      this.releaseDialog = true;
    },
    // The release goes through the case session; the case keeps its state and the next step can
    // be recorded again.
    async submitRelease({ reason }) {
      const praCase = this.praView && this.praView.praCase;
      if (!this.praSession || !praCase || !praCase.activeHold) return;
      if (this.releaseSaving) return;
      const key = releaseSubmitKey(praCase.activeHold.holdId, reason);
      // A retry of the same release reuses its commandId, so it is applied once.
      if (!this.pendingRelease || this.pendingRelease.key !== key) {
        this.pendingRelease = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingRelease;
      const caseId = praCase.id;
      this.releaseSaving = true;
      this.releaseError = "";
      try {
        const result = await this.praSession.run((revision) =>
          releasePraHold(caseId, revision, commandId, reason),
        );
        this.pendingRelease = null;
        this.praView = result;
        this.releaseDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.releaseDialog = false;
        } else {
          this.releaseError = this.holdErrorText(err, "release_failed");
        }
      } finally {
        this.releaseSaving = false;
      }
    },
    openPraFileDialog(file, access) {
      this.openPraFileChange(file, access, "replace");
    },
    // "Rimuovi" on an optional PRA document (the request's or the active hold's).
    openPraRemoveDialog(file, access) {
      this.openPraFileChange(file, access, "remove");
    },
    openPraFileChange(file, access, mode) {
      if (!this.praSession || !this.praView || !file) return;
      this.praFileError = "";
      this.praFileDialog = {
        open: true,
        file,
        reasonRequired: Boolean(access && access.reasonRequired),
        mode,
      };
    },
    // Replaces or removes a PRA document through the case session. The old file's bytes are
    // deleted by the API.
    async submitPraFile(change) {
      const current = this.praFileDialog.file;
      if (!this.praSession || !this.praView || !current || this.praFileSaving)
        return;
      const removing = this.praFileDialog.mode === "remove";
      const key = `${this.praFileDialog.mode}|${replaceSubmitKey(current.id, change)}`;
      // A retry of the same change reuses its commandId, so it is applied once.
      if (!this.pendingPraFile || this.pendingPraFile.key !== key) {
        this.pendingPraFile = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingPraFile;
      const caseId = this.praView.praCase.id;
      this.praFileSaving = true;
      this.praFileError = "";
      try {
        const result = await this.praSession.run((revision) =>
          removing
            ? removePraFile(
                caseId,
                current.id,
                revision,
                commandId,
                change.reason || undefined,
              )
            : replacePraFile(caseId, current.id, revision, commandId, change),
        );
        this.pendingPraFile = null;
        this.blobs.revoke(current.id);
        this.praView = result;
        this.praFileDialog = { ...this.praFileDialog, open: false };
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.praFileDialog = { ...this.praFileDialog, open: false };
        } else {
          this.praFileError = this.praFileErrorText(err);
        }
      } finally {
        this.praFileSaving = false;
      }
    },
    openPraCorrection(group) {
      if (!this.praSession || !this.praView || !this.praView.canCorrect) return;
      if (this.praSessionState.busy || this.praSessionState.conflict) return;
      this.praCorrectionGroup = group;
      this.praCorrectionError = "";
      this.praCorrectionPreview = null;
      this.pendingPraCorrection = null;
      this.praCorrectionDialog = true;
    },
    onPraCorrectionToggle(open) {
      this.praCorrectionDialog = open;
      if (!open) {
        // An answer to a dry run that is still on its way is ignored.
        this.praPreviewSeq += 1;
        this.praCorrectionPreview = null;
      }
    },
    // Dry run of a PRA correction through the case session: the changes and the late warning,
    // with nothing saved. `request` is { group, value } or null.
    async previewPraCorrection(request) {
      this.praPreviewSeq += 1;
      const seq = this.praPreviewSeq;
      if (!request || !this.praSession || !this.praView) {
        this.praCorrectionPreview = null;
        return;
      }
      this.praCorrectionPreview = { loading: true, error: "", data: null };
      const caseId = this.praView.praCase.id;
      try {
        const data = await this.praSession.run((revision) =>
          correctPraCase(caseId, revision, newCommandId(), {
            ...request,
            dryRun: true,
          }),
        );
        if (seq === this.praPreviewSeq) {
          this.praCorrectionPreview = { loading: false, error: "", data };
        }
      } catch (err) {
        if (seq !== this.praPreviewSeq) return;
        if (isConflictError(err)) {
          // The PRA card shows the conflict banner.
          this.praCorrectionDialog = false;
          this.praCorrectionPreview = null;
          return;
        }
        this.praCorrectionPreview = {
          loading: false,
          error: this.praCorrectionMessage(err),
          data: null,
        };
      }
    },
    // The correction goes through the case session, like the steps; a retry of the same
    // correction reuses its commandId, so it is recorded once. The state never changes.
    async submitPraCorrection(correction) {
      if (!this.praSession || !this.praView || this.praCorrectionSaving) return;
      const key = praCorrectionSubmitKey(correction);
      if (!this.pendingPraCorrection || this.pendingPraCorrection.key !== key) {
        this.pendingPraCorrection = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingPraCorrection;
      const caseId = this.praView.praCase.id;
      this.praCorrectionSaving = true;
      this.praCorrectionError = "";
      try {
        const result = await this.praSession.run((revision) =>
          correctPraCase(caseId, revision, commandId, correction),
        );
        this.pendingPraCorrection = null;
        this.praView = result;
        this.praCorrectionDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          this.praCorrectionDialog = false;
        } else {
          this.praCorrectionError = this.$t("acceptance.corrections.failed", {
            message: this.praCorrectionMessage(err),
          });
        }
      } finally {
        this.praCorrectionSaving = false;
      }
    },
    praCorrectionMessage(err) {
      const found = praCorrectionErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      if (found) {
        return this.$t(found.key, {
          other: found.other ? this.$t(found.other) : "—",
        });
      }
      return data.errorMsg || this.$t("acceptance.corrections.errors.generic");
    },
    praFileErrorText(err) {
      const key = praFileErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (key) message = this.$t(`acceptance.pra.file_change.errors.${key}`);
      else if (data.errorMsg) message = data.errorMsg;
      else message = this.$t("acceptance.files.errors.generic");
      const failed =
        this.praFileDialog.mode === "remove" ? "remove_failed" : "failed";
      return this.$t(`acceptance.pra.file_change.${failed}`, { message });
    },
    requestErrorText(err) {
      const key = requestErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (key) message = this.$t(`acceptance.pra.request.errors.${key}`);
      else if (data.errorMsg) message = data.errorMsg;
      else message = this.$t("acceptance.files.errors.generic");
      return this.$t("acceptance.pra.request.save_failed", { message });
    },
    // failed: "save_failed" (the hold) or "release_failed".
    holdErrorText(err, failed) {
      const key = holdErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (key) message = this.$t(key);
      else if (data.errorMsg) message = data.errorMsg;
      else message = this.$t("acceptance.files.errors.generic");
      return this.$t(`acceptance.pra.hold.${failed}`, { message });
    },
    receiptErrorText(err) {
      const key = receiptErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (key) message = this.$t(key);
      else if (data.errorMsg) message = data.errorMsg;
      else message = this.$t("acceptance.files.errors.generic");
      return this.$t("acceptance.pra.receipt.save_failed", { message });
    },
    crdErrorText(err) {
      const key = crdErrorKey(err);
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (key) message = this.$t(`acceptance.pra.crd.errors.${key}`);
      else if (data.errorMsg) message = data.errorMsg;
      else message = this.$t("acceptance.files.errors.generic");
      return this.$t("acceptance.pra.crd.save_failed", { message });
    },
    // GET and PATCH answer with the same view. `sent` is the form as it was when the save started.
    applyView(result, sent) {
      const serverForm = formFromDossier(result.dossier);
      this.dossier = result.dossier;
      this.car = result.car;
      this.mismatch = result.mismatch || { vin: false, plate: false };
      this.files = result.files || [];
      this.validation = result.validation || null;
      this.corrections = result.corrections || {};
      this.canCorrect = result.canCorrect === true;
      this.form = sent
        ? mergeAfterSave(this.form, sent, serverForm)
        : { ...serverForm };
      this.saved = serverForm;
    },
    setField(path, value) {
      // A frozen field only changes through a correction, whatever the input emits.
      if (this.locked || (this.isConfirmed && isFrozenPath(path))) return;
      this.form = { ...this.form, [path]: value };
    },
    async save() {
      if (!this.canSave || !this.session) return;
      const set = buildSet(this.form, this.saved);
      const key = JSON.stringify(set);
      // A retry of the same save reuses its commandId, so it is applied once.
      if (!this.pendingSave || this.pendingSave.key !== key) {
        this.pendingSave = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingSave;
      const dossierId = this.dossier.id;
      const sent = { ...this.form };
      this.saving = true;
      this.saveError = "";
      try {
        const result = await this.session.run((revision) =>
          patchDossier(dossierId, revision, commandId, set),
        );
        this.pendingSave = null;
        this.applyView(result, sent);
        if (!result.unchanged) this.historyKey += 1;
      } catch (err) {
        // A conflict shows the banner; the input stays as typed.
        if (!isConflictError(err)) this.saveError = this.errorText(err);
      } finally {
        this.saving = false;
      }
    },
    // "Annulla" on the save bar of a confirmed dossier: back to the saved values.
    cancelEdits() {
      this.form = { ...this.saved };
      this.saveError = "";
    },
    openCorrection(group) {
      if (!this.canCorrect || this.locked || this.sessionState.busy) return;
      this.correctionGroup = group;
      this.correctionError = "";
      this.correctionPreview = null;
      this.pendingCorrection = null;
      this.correctionDialog = true;
    },
    onCorrectionToggle(open) {
      this.correctionDialog = open;
      if (!open) {
        // An answer to a dry run that is still on its way is ignored.
        this.previewSeq += 1;
        this.correctionPreview = null;
      }
    },
    // Dry run of the arrival date: the new deadlines, with nothing saved. `value` is { date } or null.
    async previewCorrection(value) {
      this.previewSeq += 1;
      const seq = this.previewSeq;
      if (
        !value ||
        !this.session ||
        this.correctionGroup !== ARRIVAL_DATE_GROUP
      ) {
        this.correctionPreview = null;
        return;
      }
      this.correctionPreview = { loading: true, error: "", data: null };
      const dossierId = this.dossier.id;
      const group = this.correctionGroup;
      try {
        const data = await this.session.run((revision) =>
          correctDossier(dossierId, revision, newCommandId(), {
            group,
            value,
            dryRun: true,
          }),
        );
        if (seq === this.previewSeq) {
          this.correctionPreview = { loading: false, error: "", data };
        }
      } catch (err) {
        if (seq !== this.previewSeq) return;
        if (isConflictError(err)) {
          // The page shows the conflict banner.
          this.correctionDialog = false;
          this.correctionPreview = null;
          return;
        }
        this.correctionPreview = {
          loading: false,
          error: this.correctionMessage(err),
          data: null,
        };
      }
    },
    // A retry of the same correction reuses its commandId, so it is recorded once.
    async submitCorrection({ group, value, reason }) {
      if (!this.session || this.correctionSaving) return;
      const key = JSON.stringify({ group, value, reason });
      if (!this.pendingCorrection || this.pendingCorrection.key !== key) {
        this.pendingCorrection = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingCorrection;
      const dossierId = this.dossier.id;
      this.correctionSaving = true;
      this.correctionError = "";
      try {
        const result = await this.session.run((revision) =>
          correctDossier(dossierId, revision, commandId, {
            group,
            value,
            reason,
          }),
        );
        this.pendingCorrection = null;
        // Unsaved edits of the editable fields are kept: only fields equal to the last save take
        // the server value.
        this.applyView(result, this.saved);
        this.historyKey += 1;
        this.correctionDialog = false;
        // An arrival correction moves the deregistration deadline, so the PRA card's due date
        // and late flag change with it.
        // The depollution due date moves too.
        if (group === ARRIVAL_DATE_GROUP) {
          this.loadPraCase();
          this.loadWorkshopChecklist();
        }
      } catch (err) {
        if (isConflictError(err)) {
          // The page shows the conflict banner.
          this.correctionDialog = false;
        } else {
          this.correctionError = this.$t("acceptance.corrections.failed", {
            message: this.correctionMessage(err),
          });
        }
      } finally {
        this.correctionSaving = false;
      }
    },
    correctionMessage(err) {
      const data = (err && err.response && err.response.data) || {};
      const details = data.details || {};
      if (data.code === "arrival_after_confirm_day") {
        return this.$t(
          "acceptance.corrections.errors.arrival_after_confirm_day",
          {
            date: formatCivilDate(details.confirmDay),
          },
        );
      }
      if (data.code === "invalid_input" && details.reason === "no_change") {
        return this.$t("acceptance.corrections.errors.no_change");
      }
      // Stage 3a: with the workshop on, the arrival date stays on or before every current
      // result's performed-at day; the refused tasks are named.
      const workshopTasks = dateOrderTaskCodes(err);
      if (workshopTasks) {
        return this.$t("acceptance.corrections.errors.arrival_after_workshop", {
          tasks: workshopTasks
            .map((code) => this.$t(`workshop.task.${code}`))
            .join(", "),
        });
      }
      // Stage 2: with PRA on, the arrival date stays on or before the case's CRD date.
      if (data.code === "date_order") {
        return this.$t("acceptance.pra.corrections.errors.arrival_after_crd", {
          date: formatCivilDate(this.praCrdDate) || "—",
        });
      }
      if (data.code === "correction_blocked") {
        const items = (details.blocking || [])
          .map((item) =>
            validationLabel(item, {
              t: (key, params) => this.$t(key, params),
              lines: this.dossier ? this.dossier.checklist : [],
            }),
          )
          .join("; ");
        return this.$t("acceptance.corrections.errors.correction_blocked", {
          items,
        });
      }
      const known = [
        "reason_required",
        "not_manager",
        "not_confirmed",
        "not_correctable",
      ];
      if (known.includes(data.code)) {
        return this.$t(`acceptance.corrections.errors.${data.code}`);
      }
      if (
        data.code === "invalid_input" &&
        ERROR_REASONS.includes(details.reason)
      ) {
        return this.$t(`acceptance.errors.reasons.${details.reason}`);
      }
      return data.errorMsg || this.$t("acceptance.corrections.errors.generic");
    },
    // "Valore precedente … vedi cronologia": scroll to the log entry, or to the history card when
    // that entry is not in the loaded page of the log.
    openLog(eventId) {
      this.focusEventId = eventId;
      this.$nextTick(() => {
        const element =
          document.getElementById(`acceptance-event-${eventId}`) ||
          document.getElementById("acceptance-history");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    openConfirm() {
      if (!this.canConfirm) return;
      this.confirmError = "";
      this.confirmDialog = true;
    },
    // Confirm is irreversible, so a retry of the same click (double click, network retry) reuses
    // its commandId and is applied once. The API checks the rules again on the stored dossier.
    async submitConfirm() {
      if (!this.session || this.confirming || !this.canConfirm) return;
      if (!this.pendingConfirm)
        this.pendingConfirm = { commandId: newCommandId() };
      const { commandId } = this.pendingConfirm;
      const dossierId = this.dossier.id;
      this.confirming = true;
      this.confirmError = "";
      try {
        const result = await this.session.run((revision) =>
          confirmDossier(dossierId, revision, commandId),
        );
        this.pendingConfirm = null;
        this.applyView(result, this.saved);
        this.confirmDialog = false;
        this.historyKey += 1;
        // With PRA on, confirm opened the case in the same transaction.
        this.showDetails = false;
        this.loadPraCase();
        // With the workshop on, confirm opened the checklist in the same transaction.
        this.loadWorkshopChecklist();
      } catch (err) {
        this.handleConfirmError(err);
      } finally {
        this.confirming = false;
      }
    },
    handleConfirmError(err) {
      const data = (err && err.response && err.response.data) || {};
      if (isConflictError(err)) {
        // The page shows the conflict banner.
        this.confirmDialog = false;
        return;
      }
      this.pendingConfirm = null;
      if (data.code === "confirm_blocked" && data.details) {
        // The stored dossier changed since the last preview: show the dialog's blocked variant.
        this.validation = {
          ...(this.validation || {}),
          blocking: data.details.blocking || [],
          warnings: data.details.warnings || [],
        };
        return;
      }
      if (data.code === "already_confirmed") {
        this.confirmDialog = false;
        this.load();
        return;
      }
      const message =
        data.errorMsg || this.$t("acceptance.errors.reasons.generic");
      this.confirmError = this.$t("acceptance.confirm.failed", { message });
    },
    openWeighingDialog() {
      if (!this.canAddWeighing) return;
      this.weighingError = "";
      this.weighingDialog = true;
    },
    async submitWeighing(body) {
      if (!this.session || this.weighingSaving) return;
      const key = JSON.stringify(body);
      // A retry of the same weighing reuses its commandId, so it is recorded once.
      if (!this.pendingWeighing || this.pendingWeighing.key !== key) {
        this.pendingWeighing = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingWeighing;
      const dossierId = this.dossier.id;
      this.weighingSaving = true;
      this.weighingError = "";
      try {
        const result = await this.session.run((revision) =>
          addWeighing(dossierId, revision, commandId, body),
        );
        this.pendingWeighing = null;
        // Unsaved draft edits are kept: only fields equal to the last save take the server value.
        this.applyView(result, this.saved);
        this.weighingDialog = false;
        this.historyKey += 1;
      } catch (err) {
        if (isConflictError(err)) {
          // The page shows the conflict banner.
          this.weighingDialog = false;
        } else {
          this.weighingError = this.weighingErrorText(err);
        }
      } finally {
        this.weighingSaving = false;
      }
    },
    weighingErrorText(err) {
      const data = (err && err.response && err.response.data) || {};
      const details = data.details || {};
      let reason = details.reason;
      if (reason === "not_positive" && details.field === "tare")
        reason = "negative";
      let message;
      if (data.code === "invalid_input" && WEIGHING_REASONS.includes(reason)) {
        message = this.$t(`acceptance.weighings.errors.${reason}`, {
          max: new Intl.NumberFormat(this.$i18n.locale).format(MAX_KG),
        });
      } else if (data.errorMsg) {
        message = data.errorMsg;
      } else {
        message = this.$t("acceptance.weighings.errors.generic");
      }
      return this.$t("acceptance.weighings.save_failed", { message });
    },
    // target is { purpose: "photo", photoKind } or { purpose: "checklist", checklistCode }.
    async uploadFile(target, file) {
      if (!this.session || !this.canAddFile) return;
      const problem = checkUpload(file, target.purpose);
      if (problem) {
        this.saveError = this.$t("acceptance.files.upload_failed", {
          message: this.$t(`acceptance.files.errors.${problem}`),
        });
        return;
      }
      const slot =
        target.purpose === "photo"
          ? `photo:${target.photoKind}`
          : `checklist:${target.checklistCode}`;
      const key = [slot, file.name, file.size, file.lastModified].join("|");
      // A retry of the same upload reuses its commandId, so it is recorded once.
      if (!this.pendingUpload || this.pendingUpload.key !== key) {
        this.pendingUpload = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingUpload;
      const dossierId = this.dossier.id;
      this.uploading = slot;
      this.saveError = "";
      try {
        const result = await this.session.run((revision) =>
          uploadDossierFile(dossierId, revision, commandId, target, file),
        );
        this.pendingUpload = null;
        // Unsaved draft edits are kept: only fields equal to the last save take the server value.
        this.applyView(result, this.saved);
        this.historyKey += 1;
      } catch (err) {
        // A conflict shows the banner.
        if (!isConflictError(err)) this.saveError = this.uploadErrorText(err);
      } finally {
        this.uploading = null;
      }
    },
    uploadPhoto(kind, file) {
      this.uploadFile({ purpose: "photo", photoKind: kind }, file);
    },
    attachFile(code, file) {
      this.uploadFile({ purpose: "checklist", checklistCode: code }, file);
    },
    uploadErrorText(err) {
      const res = err && err.response;
      const data = (res && res.data) || {};
      const reason = data.details && data.details.reason;
      let key = null;
      // A proxy in front of the API may answer 413 without our JSON body.
      if (res && res.status === 413) key = "too_large";
      else if (data.code === "unsupported_type") key = "unsupported_type";
      else if (data.code === "invalid_input" && FILE_REASONS.includes(reason))
        key = reason;
      let message;
      if (key) {
        message = this.$t(`acceptance.files.errors.${key}`, {
          max: (data.details && data.details.max) || MAX_FILES_PER_TARGET,
        });
      } else if (data.errorMsg) {
        message = data.errorMsg;
      } else {
        message = this.$t("acceptance.files.errors.generic");
      }
      return this.$t("acceptance.files.upload_failed", { message });
    },
    askRemove(fileId) {
      if (!this.canAddFile) return;
      const file = this.files.find((item) => item.id === fileId);
      this.removeTarget = {
        fileId,
        name: (file && file.originalName) || "—",
      };
      this.removeReason = "";
      this.removeDialog = true;
    },
    async confirmRemove() {
      if (!this.session || this.removing || !this.removeTarget.fileId) return;
      if (this.isConfirmed && !this.removeReason.trim()) return;
      const { fileId } = this.removeTarget;
      // A retry of the same removal reuses its commandId, so it is applied once.
      if (!this.pendingRemove || this.pendingRemove.fileId !== fileId) {
        this.pendingRemove = { fileId, commandId: newCommandId() };
      }
      const { commandId } = this.pendingRemove;
      const dossierId = this.dossier.id;
      this.removing = true;
      this.saveError = "";
      try {
        const result = await this.session.run((revision) =>
          removeDossierFile(
            dossierId,
            fileId,
            revision,
            commandId,
            this.isConfirmed ? this.removeReason.trim() : undefined,
          ),
        );
        this.pendingRemove = null;
        this.blobs.revoke(fileId);
        // Unsaved draft edits are kept: only fields equal to the last save take the server value.
        this.applyView(result, this.saved);
        this.historyKey += 1;
      } catch (err) {
        // A conflict shows the banner.
        if (!isConflictError(err)) this.saveError = this.removeErrorText(err);
      } finally {
        this.removing = false;
        this.removeDialog = false;
      }
    },
    removeErrorText(err) {
      const data = (err && err.response && err.response.data) || {};
      let message;
      if (data.code === "not_uploader") {
        message = this.$t("acceptance.files.errors.not_uploader");
      } else if (data.code === "not_manager") {
        message = this.$t("acceptance.files.errors.not_manager");
      } else if (data.code === "reason_required") {
        message = this.$t("acceptance.files.errors.reason_required");
      } else if (data.errorMsg) {
        message = data.errorMsg;
      } else {
        message = this.$t("acceptance.files.errors.generic");
      }
      return this.$t("acceptance.files.remove_failed", { message });
    },
    // Images open in the preview dialog with the other images of the same photo slot or checklist
    // line (newest first); PDFs open in a new tab, where the browser's viewer is reliable.
    openFile(fileId) {
      const file = this.files.find((item) => item.id === fileId);
      if (!file || !String(file.mimetype || "").startsWith("image/")) {
        this.openInTab(fileId);
        return;
      }
      this.preview = {
        open: true,
        purpose: file.purpose,
        target: file.purpose === "photo" ? file.photoKind : file.checklistCode,
        startId: fileId,
      };
    },
    // Opens a private file in a new tab. The tab is opened before the download so the browser
    // does not treat it as a popup.
    async openInTab(fileId) {
      const tab = window.open("", "_blank");
      if (!tab) {
        this.saveError = this.$t("acceptance.files.open_failed");
        return;
      }
      tab.opener = null;
      try {
        tab.location.href = await this.blobs.get(fileId);
      } catch (err) {
        tab.close();
        this.saveError = this.$t("acceptance.files.open_failed");
      }
    },
    errorText(err) {
      const data = (err && err.response && err.response.data) || {};
      const reason = data.details && data.details.reason;
      let message;
      if (data.code === "invalid_input" && ERROR_REASONS.includes(reason)) {
        message = this.$t(`acceptance.errors.reasons.${reason}`);
      } else if (data.code === "field_frozen") {
        message = this.$t("acceptance.errors.reasons.field_frozen");
      } else if (data.errorMsg) {
        message = data.errorMsg;
      } else {
        message = this.$t("acceptance.errors.reasons.generic");
      }
      return this.$t("acceptance.errors.save_failed", { message });
    },
    scrollTo(code) {
      const section = CODE_SECTIONS[code];
      const element =
        section && document.getElementById(`acceptance-${section}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    stay() {
      this.leaveDialog = false;
      if (this.pendingLeave) this.pendingLeave(false);
      this.pendingLeave = null;
    },
    leave() {
      this.leaveDialog = false;
      this.leaveConfirmed = true;
      if (this.pendingLeave) this.pendingLeave();
      this.pendingLeave = null;
    },
  },
};
</script>

<style scoped>
/* Grey page behind white cards, so each card reads as its own block. */
.acceptance-page {
  min-height: 100%;
  background: #f5f5f5; /* Vuetify grey-lighten-4 */
}
/* PRA layout, expanded: the summary strip and the Stage 1 cards form one white block; the cards
   inside it are flat with a light border instead of floating on the page. */
.acceptance-summary-open {
  margin-bottom: 0 !important;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.acceptance-details {
  margin-bottom: 16px;
  padding: 16px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0 0 4px 4px;
}
.acceptance-details :deep(.v-card) {
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.acceptance-back {
  color: inherit;
  text-decoration: none;
}
.acceptance-back:hover {
  text-decoration: underline;
}
.acceptance-sticky {
  position: sticky;
  top: 16px;
}
.acceptance-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
